import React, {useEffect, useState, useContext, useRef} from 'react';
import PropTypes from 'prop-types';
import {connect} from 'react-redux';
import ArrowLayer from './ArrowLayer';
import {ArrowDirectionType, ArrowLineType, CardType, TaskType} from '../../../constants/dataEnum';
import './StickyArrowLayerContainer.scss';
import {findKeyByValue, filterToArray} from '../../../lib/objectHelper';
import {ProtocolEditorContext} from '../ContextProviders/ProtocolEditorContext';
import {LabwareZoomContext} from '../ContextProviders/LabwareZoomContext';

const LabwareSide = {
  TOP: 'Top',
  BOTTOM: 'Bottom',
  LEFT: 'Left',
  TOP_LEFT: 'TopLeft',
  BOTTOM_LEFT: 'BottomLeft',
  RIGHT: 'Right',
  TOP_RIGHT: 'TopRight',
  BOTTOM_RIGHT: 'BottomRight',
};

const DefaultStartArrowPosition = {
  1: LabwareSide.RIGHT,
  2: LabwareSide.RIGHT,
  3: LabwareSide.LEFT,
  4: LabwareSide.RIGHT,
  5: LabwareSide.RIGHT,
  6: LabwareSide.LEFT,
};

const EndArrowPosition = {
  1: {
    1: {position: LabwareSide.BOTTOM_RIGHT, direction: ArrowDirectionType.LEFT},
    2: {position: LabwareSide.LEFT, direction: ArrowDirectionType.RIGHT},
    3: {position: LabwareSide.LEFT, direction: ArrowDirectionType.RIGHT},
    4: {position: LabwareSide.RIGHT, direction: ArrowDirectionType.LEFT},
    5: {position: LabwareSide.LEFT, direction: ArrowDirectionType.RIGHT, type: ArrowLineType.CURVED},
    6: {position: LabwareSide.LEFT, direction: ArrowDirectionType.RIGHT},
  },
  2: {
    1: {position: LabwareSide.RIGHT, direction: ArrowDirectionType.LEFT},
    2: {position: LabwareSide.BOTTOM_RIGHT, direction: ArrowDirectionType.LEFT},
    3: {position: LabwareSide.LEFT, direction: ArrowDirectionType.RIGHT},
    4: {position: LabwareSide.TOP, direction: ArrowDirectionType.LEFT},
    5: {position: LabwareSide.TOP, direction: ArrowDirectionType.LEFT},
    6: {position: LabwareSide.TOP, direction: ArrowDirectionType.RIGHT},
  },
  3: {
    1: {position: LabwareSide.RIGHT, direction: ArrowDirectionType.LEFT},
    2: {position: LabwareSide.RIGHT, direction: ArrowDirectionType.LEFT},
    3: {position: LabwareSide.BOTTOM_LEFT, direction: ArrowDirectionType.RIGHT},
    4: {position: LabwareSide.RIGHT, direction: ArrowDirectionType.LEFT},
    5: {position: LabwareSide.RIGHT, direction: ArrowDirectionType.LEFT, type: ArrowLineType.CURVED},
    6: {position: LabwareSide.LEFT, direction: ArrowDirectionType.RIGHT},
  },
  4: {
    1: {position: LabwareSide.RIGHT, direction: ArrowDirectionType.RIGHT},
    2: {position: LabwareSide.RIGHT, direction: ArrowDirectionType.LEFT},
    3: {position: LabwareSide.LEFT, direction: ArrowDirectionType.LEFT},
    4: {position: LabwareSide.BOTTOM_RIGHT, direction: ArrowDirectionType.LEFT},
    5: {position: LabwareSide.LEFT, direction: ArrowDirectionType.RIGHT},
    6: {position: LabwareSide.LEFT, direction: ArrowDirectionType.RIGHT},
  },
  5: {
    1: {
      position: LabwareSide.RIGHT,
      direction: ArrowDirectionType.LEFT,
      source: LabwareSide.LEFT,
      type: ArrowLineType.CURVED,
    },
    2: {position: LabwareSide.BOTTOM, direction: ArrowDirectionType.LEFT},
    3: {
      position: LabwareSide.LEFT,
      direction: ArrowDirectionType.RIGHT,
      source: LabwareSide.RIGHT,
      type: ArrowLineType.CURVED,
    },
    4: {position: LabwareSide.RIGHT, direction: ArrowDirectionType.LEFT, source: LabwareSide.LEFT},
    5: {position: LabwareSide.BOTTOM_RIGHT, direction: ArrowDirectionType.LEFT},
    6: {position: LabwareSide.LEFT, direction: ArrowDirectionType.RIGHT, source: LabwareSide.RIGHT},
  },
  6: {
    1: {position: LabwareSide.RIGHT, direction: ArrowDirectionType.RIGHT},
    2: {position: LabwareSide.BOTTOM, direction: ArrowDirectionType.LEFT},
    3: {position: LabwareSide.LEFT, direction: ArrowDirectionType.LEFT},
    4: {position: LabwareSide.RIGHT, direction: ArrowDirectionType.LEFT},
    5: {position: LabwareSide.RIGHT, direction: ArrowDirectionType.LEFT},
    6: {position: LabwareSide.BOTTOM_LEFT, direction: ArrowDirectionType.RIGHT},
  },
};

function StickyArrowLayerContainer({
  cardStateTasks,
  cardStateCards,
  labwareState,
  selectedCard,
  isArrowSticky = false,
  isArrowShowing = false,
  setIsArrowShowing,
  mousePosition,
}) {
  const {deckLabwareRefs, plateLabwareRefs, zoomWidth, zoomHeight} = useContext(ProtocolEditorContext) || {};
  const {isLabwareZoom} = useContext(LabwareZoomContext) || {};

  const defaultArrowType = ArrowLineType.BOW;

  const [arrowDefinitions, setArrowDefinitions] = useState([]);

  const [mouseAnchorPos, setMouseAnchorPos] = useState({x: null, y: null});
  const [mouseArrowDirection, setMouseArrowDirection] = useState(ArrowDirectionType.LEFT);
  const [mouseArrowType, setMouseArrowType] = useState(defaultArrowType);
  const aspirateTasksRef = useRef([]);
  const dispenseTaskRef = useRef([]);

  useEffect(() => {
    const aspirateTasks = filterToArray(cardStateTasks, {
      ID: selectedCard?.tasks,
      type: TaskType.ASPIRATE,
    });

    const dispenseTasks = filterToArray(cardStateTasks, {
      ID: selectedCard?.tasks,
      type: TaskType.DISPENSE,
    });

    aspirateTasksRef.current = aspirateTasks;
    dispenseTaskRef.current = dispenseTasks;
  }, [cardStateTasks, cardStateCards, selectedCard]);

  useEffect(() => {
    if (isArrowSticky && isArrowShowing) {
      setMouseArrowDirection(
        mouseAnchorPos.x > mousePosition.x - 5 ? ArrowDirectionType.LEFT : ArrowDirectionType.RIGHT,
      );
      setMouseArrowType(defaultArrowType);
    }
  }, [mousePosition.x, mouseAnchorPos.x, isArrowSticky, isArrowShowing, defaultArrowType]);

  function generateStartPositionPoint(side, position) {
    let x;
    let y;

    switch (side) {
      case LabwareSide.LEFT:
        x = position.left - 5;
        y = position.top + position.height / 2;
        break;
      case LabwareSide.RIGHT:
        x = position.right + 5;
        y = position.top + position.height / 2;
        break;
      case LabwareSide.TOP:
        x = position.left + position.width / 2;
        y = position.top;
        break;
      case LabwareSide.BOTTOM:
        x = position.left + position.width / 2;
        y = position.bottom + 10;
        break;
      default:
        x = 0;
        y = 0;
        break;
    }

    return {x, y};
  }

  function generateEndPositionPoint(side, position) {
    let x;
    let y;

    switch (side) {
      case LabwareSide.LEFT:
        x = position.left - 5;
        y = position.top + position.height / 2;
        break;
      case LabwareSide.BOTTOM_LEFT:
        x = position.left - 5;
        y = position.top + position.height * (3 / 4);
        break;
      case LabwareSide.TOP_LEFT:
        x = position.left - 5;
        y = position.top + position.height * (1 / 4);
        break;
      case LabwareSide.RIGHT:
        x = position.right + 5;
        y = position.top + position.height / 2;
        break;
      case LabwareSide.BOTTOM_RIGHT:
        x = position.right + 5;
        y = position.top + position.height * (3 / 4);
        break;
      case LabwareSide.TOP_RIGHT:
        x = position.right + 5;
        y = position.top + position.height * (1 / 4);
        break;
      case LabwareSide.TOP:
        x = position.left + position.width / 2;
        y = position.top;
        break;
      case LabwareSide.BOTTOM:
        x = position.left + position.width / 2;
        y = position.bottom + 10;
        break;
      default:
        x = 0;
        y = 0;
        break;
    }

    return {x, y};
  }

  const getAnchorPosition = (side, sourceDeckNumber) => {
    const sourceLabware = labwareState[sourceDeckNumber];
    const sourceDeckPos = deckLabwareRefs[sourceDeckNumber].current.getBoundingClientRect();
    const {x: xSource, y: ySource} = generateStartPositionPoint(side, sourceDeckPos);
    const newAnchorPos = {x: xSource, y: ySource};

    if (sourceLabware.isSelected) {
      if (side === LabwareSide.RIGHT) {
        const factor = sourceDeckPos.width * 0.015;

        newAnchorPos.x = xSource - factor;
        newAnchorPos.y = ySource;
      }
    }

    return newAnchorPos;
  };

  const generateArrowDefinition = (id, anchorPosition, sourceDeckNumber, destinationDeckNumber) => {
    let newAnchorPosition = anchorPosition;

    const destinationLabware = labwareState[destinationDeckNumber];
    const destinationRef = deckLabwareRefs[destinationDeckNumber].current;
    const destinationDeckPos = destinationRef.getBoundingClientRect();
    const arrowPos = EndArrowPosition[sourceDeckNumber];

    const {position, direction, source, type} = arrowPos[destinationDeckNumber];
    const {x, y} = generateEndPositionPoint(position, destinationDeckPos);
    const endPosition = {x, y};

    if (destinationLabware.isSelected) {
      const factor = destinationDeckPos.width * 0.023;

      endPosition.x = x - factor;
    }

    if (source != null) {
      newAnchorPosition = getAnchorPosition(source, sourceDeckNumber);
    }

    const arrowType = type != null ? type : defaultArrowType;

    return {
      key: `sticky-arrow-${id}`,
      fromX: newAnchorPosition.x,
      fromY: newAnchorPosition.y,
      toX: endPosition.x,
      toY: endPosition.y,
      direction,
      line: arrowType,
    };
  };

  useEffect(() => {
    const aspirateTask = aspirateTasksRef.current[0];
    const dispenseTask = dispenseTaskRef.current[0];

    const newArrowDefinitions = [];
    let anchorPosition = {x: null, y: null};

    switch (selectedCard?.type) {
      case CardType.BROADCAST: {
        let sourceDeckNumber = 1;

        if (aspirateTask != null) {
          sourceDeckNumber = findKeyByValue(labwareState, {LabwareID: aspirateTask.labwareID});

          if (deckLabwareRefs[sourceDeckNumber] != null) {
            const side = DefaultStartArrowPosition[sourceDeckNumber];
            anchorPosition = getAnchorPosition(side, sourceDeckNumber);

            setMouseAnchorPos(anchorPosition);
            setIsArrowShowing(true);
          }
        }

        if (dispenseTaskRef.current.length) {
          const destinationDeckNumberMap = {};

          dispenseTaskRef.current.forEach((dTask) => {
            const destinationDeckNumber = findKeyByValue(labwareState, {LabwareID: dTask.labwareID});

            if (!(destinationDeckNumber in destinationDeckNumberMap)) {
              destinationDeckNumberMap[destinationDeckNumber] = true;
            }
          });

          Object.keys(destinationDeckNumberMap).forEach((destinationDeckNumber, i) => {
            if (deckLabwareRefs[destinationDeckNumber] != null) {
              newArrowDefinitions.push(
                generateArrowDefinition(i, anchorPosition, sourceDeckNumber, destinationDeckNumber),
              );
            }
          });
        }

        setArrowDefinitions(newArrowDefinitions);

        break;
      }
      case CardType.POOLING: {
        if (selectedCard?.type === CardType.POOLING && (!aspirateTask?.volume || aspirateTask?.volume === 0)) {
          setArrowDefinitions([]);
          setIsArrowShowing(false);

          break;
        }

        if (dispenseTask != null) {
          const destinationDeckNumber = findKeyByValue(labwareState, {LabwareID: dispenseTask.labwareID});

          if (deckLabwareRefs[destinationDeckNumber] != null) {
            if (aspirateTasksRef.current.length) {
              const sourceDeckNumberMap = {};

              aspirateTasksRef.current.forEach((aTask) => {
                const sourceDeckNumber = findKeyByValue(labwareState, {LabwareID: aTask.labwareID});

                if (!(sourceDeckNumber in sourceDeckNumberMap)) {
                  sourceDeckNumberMap[sourceDeckNumber] = true;
                }
              });

              Object.keys(sourceDeckNumberMap).forEach((sourceDeckNumber, i) => {
                if (deckLabwareRefs[sourceDeckNumber] != null) {
                  const side = DefaultStartArrowPosition[sourceDeckNumber];

                  anchorPosition = getAnchorPosition(side, sourceDeckNumber);

                  newArrowDefinitions.push(
                    generateArrowDefinition(i, anchorPosition, sourceDeckNumber, destinationDeckNumber),
                  );
                }
              });
            }
          }
        }

        setMouseAnchorPos(anchorPosition);
        setArrowDefinitions(newArrowDefinitions);
        setIsArrowShowing(true);

        break;
      }
      default:
        setArrowDefinitions([]);
        setIsArrowShowing(false);

        break;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    aspirateTasksRef.current,
    dispenseTaskRef.current,
    deckLabwareRefs,
    labwareState,
    plateLabwareRefs,
    defaultArrowType,
    setIsArrowShowing,
    mousePosition,
    isLabwareZoom,
    selectedCard,
    zoomWidth,
    zoomHeight,
  ]);

  const mouseArrowDefinitions = [
    {
      key: 'sticky-arrow-mouse',
      fromX: mouseAnchorPos.x,
      fromY: mouseAnchorPos.y,
      toX: mousePosition.x,
      toY: mousePosition.y,
      direction: mouseArrowDirection,
      line: mouseArrowType,
    },
  ];

  const wrapperClass = isArrowSticky ? 'sticky-arrow sticky' : 'sticky-arrow';

  const renderArrow = () => {
    let retVal;

    if (isArrowShowing && mouseAnchorPos.x != null && mouseAnchorPos.y != null) {
      retVal = (
        <div className={wrapperClass}>
          {isArrowSticky && (
            <ArrowLayer arrowDefinitions={mouseArrowDefinitions} id="sticky-arrow-1" isArrowFollowMouse />
          )}
          {arrowDefinitions.length > 0 && <ArrowLayer arrowDefinitions={arrowDefinitions} id="sticky-arrow-2" />}
          {isArrowSticky && (
            <div className="arrow-anchor" style={{left: mouseAnchorPos.x - 5, top: mouseAnchorPos.y - 70}} />
          )}
          {arrowDefinitions.map((e, i) => (
            // eslint-disable-next-line react/no-array-index-key
            <div key={`arrow-anchor-${i}`} className="arrow-anchor" style={{left: e.fromX - 5, top: e.fromY - 70}} />
          ))}
        </div>
      );
    } else {
      retVal = null;
    }

    return retVal;
  };

  return renderArrow();
}

StickyArrowLayerContainer.propTypes = {
  cardStateTasks: PropTypes.object,
  cardStateCards: PropTypes.object,
  labwareState: PropTypes.object,
  selectedCard: PropTypes.object,
  isArrowSticky: PropTypes.bool,
  isArrowShowing: PropTypes.bool,
  setIsArrowShowing: PropTypes.func,
  mousePosition: PropTypes.object,
};

StickyArrowLayerContainer.defaultProps = {
  cardStateTasks: {},
  cardStateCards: {},
  labwareState: {},
  selectedCard: {},
  isArrowSticky: false,
  isArrowShowing: false,
  setIsArrowShowing: () => {},
  mousePosition: {x: null, y: null},
};

function stickyArrowLayerContainerMapStateToProps(state) {
  return {
    cardStateCards: state.cardState.cards,
    cardStateTasks: state.cardState.tasks,
    labwareState: state.labwareState,
  };
}
export default connect(stickyArrowLayerContainerMapStateToProps, null)(StickyArrowLayerContainer);
